import { Trans, useLingui } from "@lingui/react/macro";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import AppLayout from "@/components/AppLayout";
import CollapsibleSection from "@/components/CollapsibleSection";
import { useAppConfig } from "@/contexts/AppConfigContext";
import { THEME_LABELS, THEME_PRESETS, type ThemeName } from "@/theme/presets";
import CodePreview from "./CodePreview";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "de", label: "German" },
];

const APPEARANCE_FIELDS = [
  {
    name: "primaryColor",
    label: "Primary Color",
    placeholder: "#4E7944",
  },
  {
    name: "backgroundColor",
    label: "Background Color",
    placeholder: "#F1F3EF",
  },
  {
    name: "borderRadius",
    label: "Border Radius",
    placeholder: "30px",
  },
] as const;

function Install() {
  const { t } = useLingui();
  const { campgroundSlug: currentCampgroundSlug } = useAppConfig();
  const [campgroundSlug, setcampgroundSlug] = useState(currentCampgroundSlug);
  const [language, setLanguage] = useState("en");
  const [themeName, setThemeName] = useState<ThemeName>("campnerd");
  const [aid, setAid] = useState<string | null>(null);
  const [demoMode, setDemoMode] = useState(false);
  const [appearance, setAppearance] = useState({
    primaryColor: "",
    backgroundColor: "",
    borderRadius: "",
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);

  const updateAppearanceField = (name: string, value: string) => {
    setAppearance((prev) => ({ ...prev, [name]: value }));
  };

  const buildWidgetConfig = () => {
    interface WidgetConfig {
      campgroundSlug: string;
      language: string;
      aid: string | null;
      demoMode: boolean;
      appearance: {
        theme: ThemeName;
        [key: string]: string | ThemeName;
      };
    }

    const config: WidgetConfig = {
      campgroundSlug: campgroundSlug,
      language: language,
      aid: aid,
      demoMode: demoMode,
      appearance: {
        theme: themeName,
      },
    };

    Object.entries(appearance).forEach(([key, value]) => {
      if (value) {
        config.appearance[key] = value;
      }
    });

    return config;
  };

  // Always reflects the latest buildWidgetConfig without re-registering the
  // message listener on every state change.
  const buildWidgetConfigRef = useRef(buildWidgetConfig);
  buildWidgetConfigRef.current = buildWidgetConfig;

  const generateEmbedCode = () => {
    const config = buildWidgetConfig();

    return `<script>
  (function(w, d) {
    w.CampNerd = w.CampNerd || function() {
      (w.CampNerd.q = w.CampNerd.q || []).push(arguments);
    };
    w.CampNerd.q = w.CampNerd.q || [];

    CampNerd('init', ${JSON.stringify(config, null, 2).replace(/\n/g, "\n    ")});

    var script = d.createElement('script');
    script.async = true;
    script.src = '${window.location.origin}/widget.js';
    var currentScript = d.currentScript;
    currentScript.parentNode.insertBefore(script, currentScript.nextSibling);
  })(window, document);
</script>`;
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== "campnerd.ready") return;
      const contentWindow = previewIframeRef.current?.contentWindow;
      if (event.source !== contentWindow || !contentWindow) return;
      contentWindow.postMessage(
        { type: "campnerd.init", config: buildWidgetConfigRef.current() },
        "*",
      );
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <AppLayout>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 3 }}>
        <Typography variant="h2">
          <Trans>Install Widget</Trans>
        </Typography>

        <Typography variant="body1" color="text.secondary">
          <Trans>
            To install the booking widget on your site, configure your settings below, copy the
            generated code, and paste it into your website where you want the widget to appear.
          </Trans>
        </Typography>

        <TextField
          label={t`Campground Slug`}
          value={campgroundSlug}
          onChange={(e) => setcampgroundSlug(e.target.value)}
          fullWidth
          required
        />

        <TextField
          label={t`Affiliate ID`}
          value={aid}
          onChange={(e) => setAid(e.target.value)}
          fullWidth
        />

        <TextField
          select
          label={t`Language`}
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          fullWidth
        >
          {LANGUAGES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Trans>{option.label}</Trans>
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label={t`Theme`}
          value={themeName}
          onChange={(e) => setThemeName(e.target.value as ThemeName)}
          fullWidth
        >
          {(Object.keys(THEME_PRESETS) as ThemeName[]).map((name) => (
            <MenuItem key={name} value={name}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: THEME_PRESETS[name].swatchColor,
                    border: "2px solid",
                    borderColor: "divider",
                  }}
                />
                <Typography>{THEME_LABELS[name]}</Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>

        <FormControlLabel
          control={<Switch checked={demoMode} onChange={(e) => setDemoMode(e.target.checked)} />}
          label={t`Demo mode (no real bookings)`}
          sx={{ ml: 0, columnGap: 1 }}
        />

        <CollapsibleSection
          title={
            <Typography variant="h6" fontWeight={600}>
              <Trans>Customize Theme</Trans>
            </Typography>
          }
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <Trans>Override theme properties by specifying custom values below</Trans>
            </Typography>

            {APPEARANCE_FIELDS.map((field) => {
              const getLabel = (): string => {
                switch (field.name) {
                  case "primaryColor":
                    return t`Primary Color`;
                  case "backgroundColor":
                    return t`Background Color`;
                  case "borderRadius":
                    return t`Border Radius`;
                  default:
                    return (field as { label: string }).label;
                }
              };
              return (
                <TextField
                  key={field.name}
                  label={getLabel()}
                  value={appearance[field.name as keyof typeof appearance]}
                  onChange={(e) => updateAppearanceField(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  fullWidth
                  size="small"
                />
              );
            })}
          </Box>
        </CollapsibleSection>

        <CodePreview code={generateEmbedCode()} onPreview={() => setPreviewOpen(true)} />
      </Box>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">
              <Trans>Widget Preview</Trans>
            </Typography>
            <IconButton onClick={() => setPreviewOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              width: "100%",
              height: 500,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <iframe
              ref={previewIframeRef}
              src={`${window.location.origin}/${language}/${campgroundSlug}/`}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: 4,
              }}
              title={t`Widget Preview`}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

export default Install;
